import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from '../services/firebase';

const AuthUserContext = createContext({});

/**
 * Fonte única do usuário: sessão do Firebase Auth + documento `users/{uid}`.
 *
 * Antes cada tela repetia o mesmo bloco (`onAuthStateChanged` + `getDoc` em
 * `users/{uid}`) com o seu próprio `isLoading`. Como as camadas montam em
 * cascata, essas esperas apareciam em sequência e o usuário via vários
 * loadings seguidos para carregar exatamente o mesmo dado. Aqui a leitura
 * acontece uma vez, antes de qualquer tela renderizar, e quem precisa apenas
 * consome o contexto.
 *
 * Expõe:
 * - `authUser`: sessão do Firebase Auth (inclui anônimos), ou `null`
 * - `user`: documento `users/{uid}` (só para usuário logado de verdade)
 * - `userId`: uid da sessão atual
 * - `isLogged`: autenticado e NÃO anônimo
 * - `isAnonymous`: sessão anônima
 * - `isLoadingUser`: primeira resolução da sessão + documento
 * - `refetchUser`: relê o documento após gravações (perfil, compras etc.)
 */
export const AuthUserProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [user, setUser] = useState();
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  const loadUserDoc = useCallback(async (userId) => {
    const docSnap = await getDoc(doc(firestore, 'users', userId));
    const data = docSnap.exists() ? docSnap.data() : undefined;
    setUser(data);
    return data;
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (nextAuthUser) => {
      setAuthUser(nextAuthUser ?? null);

      if (nextAuthUser && !nextAuthUser.isAnonymous) {
        try {
          await loadUserDoc(nextAuthUser.uid);
        } catch (error) {
          console.error(error);
        }
      } else {
        // Sessão anônima (ou encerrada) não tem documento em `users`.
        setUser(undefined);

        if (!nextAuthUser) {
          // Nenhum usuário está logado, realizar login anônimo.
          try {
            await signInAnonymously(auth);
          } catch (error) {
            // Houve um erro ao realizar login anônimo. Ainda assim liberamos a
            // tela: manter o loading ligado aqui travava o app no spinner.
            console.error(error);
          }
        }
      }

      setIsLoadingUser(false);
    });

    return () => unsubscribe();
  }, [loadUserDoc]);

  const refetchUser = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.isAnonymous) return undefined;
    return loadUserDoc(currentUser.uid);
  }, [loadUserDoc]);

  const value = useMemo(
    () => ({
      authUser,
      user,
      userId: authUser?.uid,
      isLogged: !!authUser && !authUser.isAnonymous,
      isAnonymous: !!authUser?.isAnonymous,
      isLoadingUser,
      refetchUser,
    }),
    [authUser, user, isLoadingUser, refetchUser]
  );

  return (
    <AuthUserContext.Provider value={value}>
      {children}
    </AuthUserContext.Provider>
  );
};

export const useAuthUser = () => useContext(AuthUserContext);
